import gql from 'graphql-tag';
import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import { LISTING_STATES, MINT_CONDITIONS, ENDED_LISTING_STATES } from './shared/constants';
import URLHelpers from './shared/url_helpers';

import { withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';

import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import AddToCartButton, { AddToCartButtonFragment } from '@reverbdotcom/commons/src/components/add_to_cart_button';
import Item2ListingState from './item2_listing_state';
import { Item2Stats, Item2StatsFragment } from './item2_stats';

import CreateOfferButton, { ListingCreateOfferButtonFragment } from '@reverbdotcom/commons/src/offers/create_offer_button';
import PriceWithShipping, { Item2PriceWithShippingFragment } from '@reverbdotcom/commons/src/components/price_with_shipping';
import { ListingQuantitySelector } from '@reverbdotcom/commons/src/components/ListingQuantitySelector';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import CompareOtherSellerLink, { CompareOtherSellersLinkFragment } from './compare_other_sellers_link';
import { Item2WhyToBuySignals, SignalsFragment } from './item2_nudge_signals';
import { Item2EndedListingSearchButton } from './item2_ended_listing_search_button';

import { flashSuccess } from '@reverbdotcom/commons/src/flash';

import {
  Item2PricingData,
  Item2PricingNegotiationsData,
  core_apimessages_CartItem as CartItem,
  core_apimessages_Listing_ListingType as ListingType,
  core_apimessages_Offer_OfferStatus as OfferStatus,
} from '../schemas/rql';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { BuyItNowButton, BuyItNowButtonFragment } from './BuyItNowButton';
import { ListingFinancingCallout, ListingFinancingCalloutFragment } from './ListingFinancingCallout';
import { DisplayStyle, WatchBadge } from '@reverbdotcom/commons/src/components/watch_badge';
import { WatchParentComponentNames } from '@reverbdotcom/commons/src/event_tracking/watchlist';
import { isBuyItNowVisible } from './isBuyItNowVisible';

type Negotiation = Item2PricingNegotiationsData.Fragment;
type Listing = Item2PricingData.Fragment;

interface IProps {
  listing: Listing;
  negotiations?: Negotiation[];
  cartItems?: CartItem[];
  onFeaturedListing?: boolean;
  userTotalOffersCount?: number;
  watchPrompt?: boolean;
  showFivePercentPriceDrop?: boolean;
  showKlarnaCallout?: boolean;
}

export const Item2PricingFragment = gql`
  fragment Item2PricingData on Listing {
    _id
    id
    listingType
    inventory
    hasInventory
    maxBuyerQuantity
    make
    model
    state
    condition {
      conditionSlug
    }
    shop {
      _id
      address {
        countryCode
        country {
          name
        }
      }
    }
    ...AddToCartButtonData
    ...Item2PriceWithShippingData
    ...CompareOtherSellersLinkData
    ...ListingFinancingCalloutFields
    ...SignalsData
    ...Item2StatsData
    ...BuyItNowButtonFields
    ...ListingCreateOfferButtonData
  }
  ${AddToCartButtonFragment}
  ${Item2PriceWithShippingFragment}
  ${CompareOtherSellersLinkFragment}
  ${ListingFinancingCalloutFragment}
  ${SignalsFragment}
  ${Item2StatsFragment}
  ${BuyItNowButtonFragment}
  ${ListingCreateOfferButtonFragment}
`;

export const Item2PricingNegotiationsFragment = gql`
fragment Item2PricingNegotiationsData on Negotiation {
  _id
  id
  lastOffer {
    status
  }
}
`;

const ACTIVE_OFFER_STATUSES = [
  OfferStatus.ACTIVE,
  OfferStatus.COUNTERED,
];

const INACTIVE_OFFER_STATUSES = [
  OfferStatus.ACCEPTED,
  OfferStatus.REJECTED,
  OfferStatus.EXPIRED,
];

function isLive(listing: Item2PricingData.Fragment): boolean {
  return listing.state === LISTING_STATES.LIVE;
}

function isEndedListing(listing: Item2PricingData.Fragment): boolean {
  return Object.values(ENDED_LISTING_STATES).includes(listing.state);
}

function notInMintCondition(listing: Item2PricingData.Fragment): boolean {
  return !MINT_CONDITIONS.includes(listing.condition.conditionSlug);
}

function offerEnabled(listing: Item2PricingData.Fragment): boolean {
  return listing.listingType === ListingType.MAKE_AN_OFFER;
}

function showActiveOffer({ listing, negotiations }: { listing: Listing, negotiations: Negotiation[] }) {
  return offerEnabled(listing)
    && !isEmpty(negotiations)
    && !!negotiations.find(n => ACTIVE_OFFER_STATUSES.includes(n.lastOffer.status));
}

function showOfferHistory({ listing, negotiations }: { listing: Listing, negotiations: Negotiation[] }) {
  return offerEnabled(listing)
    && !isEmpty(negotiations)
    && !!negotiations.find(n => INACTIVE_OFFER_STATUSES.includes(n.lastOffer.status));
}

function showMakeOffer({ listing, negotiations }: { listing: Listing, negotiations: Negotiation[] }) {
  if (offerEnabled(listing) && isEmpty(negotiations)) {
    return true;
  }

  return showOfferHistory({ listing, negotiations });
}

function offerUrl(negotiation: Negotiation): string {
  return URLHelpers.offerPath({
    offerId: negotiation.id,
    status: 'all',
  });
}

function listingOffersPath(listing: Item2PricingData.Fragment): string {
  return URLHelpers.listingOffersPath({
    product_id: listing.id,
    status: 'all',
  });
}

function onOfferSuccess(message) {
  flashSuccess(message);
}

function endedListingStatusIndicatorText(listing: Item2PricingData.Fragment) {
  switch (listing.state) {
    case LISTING_STATES.ENDED:
      return I18n.t('discovery.item2Pricing.statusIndicatorEnded');
    case LISTING_STATES.ORDERED:
    case LISTING_STATES.SOLD_OUT:
      return I18n.t('discovery.item2Pricing.statusIndicatorSold');
    case LISTING_STATES.SELLER_UNAVAILABLE:
      return I18n.t('discovery.item2Pricing.statusIndicatorUnavailable');
    default:
      return null;
  }
}

export function Item2Pricing({
  listing,
  negotiations = [],
  onFeaturedListing = false,
  cartItems = [],
  userTotalOffersCount = undefined,
  watchPrompt = false,
  showFivePercentPriceDrop = false,
  showKlarnaCallout = false,
}: IProps) {
  const [quantity, setQuantity] = React.useState(1);

  const isListingPreorder = !!listing.preorderInfo && listing.preorderInfo.onPreorder;

  const isEndedListingStatusIndicatorEnabled = useExpEnabled(experiments.ENDED_LISTING_STATUS_INDICATOR) && !isLive(listing);

  const showBuyItNowButton = isBuyItNowVisible({
    isFeaturedListing: onFeaturedListing,
    isListingPreorder,
  });

  const classes = classNames(
    'item2-pricing',
    'item2-pricing--signal-system',
    { 'item2-pricing--buy-it-now': showBuyItNowButton },
  );

  if (!listing) {
    return null;
  }

  return (
    <div className={classes}>
      <div className="item2-pricing__amount">
        <PriceWithShipping
          listing={listing}
          cartItems={cartItems}
          inlineRibbon
          showCombinedRate
          showFivePercentPriceDrop={showFivePercentPriceDrop}
        />
      </div>
      {isLive(listing) && (
        <ListingFinancingCallout
          listing={listing}
          showKlarnaCallout={showKlarnaCallout}
        />
      )}
      {isLive(listing) &&
        <Item2WhyToBuySignals listing={listing} />
      }
      {listing.hasInventory && isLive(listing) && notInMintCondition(listing) &&
        <div className="item2-pricing__quantity">
          <ListingQuantitySelector
            onQuantityChange={setQuantity}
            totalInStock={listing.inventory}
            quantity={quantity}
            maxBuyerQuantity={listing.maxBuyerQuantity}
            price={listing.pricing?.buyerPrice}
            onPreorder={isListingPreorder}
          />
        </div>
      }
      {isEndedListing(listing) && !isEndedListingStatusIndicatorEnabled &&
        <div className="item2-sold-text">
          <p>{I18n.t('discovery.item2Pricing.gearIsGone')}</p>
        </div>
      }
      <div className="item2-pricing__actions">
        {isEndedListing(listing) && !isEndedListingStatusIndicatorEnabled &&
          <Item2EndedListingSearchButton make={listing.make} model={listing.model} listingId={listing.id} />
        }
        {!isEndedListing(listing) && !isLive(listing) && !isEndedListingStatusIndicatorEnabled &&
          <Item2ListingState state={listing.state} />
        }
        {isLive(listing) &&
          <>
            {showBuyItNowButton &&
              <BuyItNowButton
                listing={listing}
              />
            }
            <div>
              <AddToCartButton
                listing={listing}
                quantity={quantity}
                disabled={!isLive}
                parentComponentName={onFeaturedListing ? 'FeaturedListing' : 'ItemPage'}
              />
            </div>
            <Item2ListingState state={listing.state} />
            {showActiveOffer({ listing, negotiations }) &&
              <div className="margin-half">
                <a
                  data-view-offer
                  className="button width-100"
                  href={offerUrl(negotiations[0])}
                >
                  {I18n.t('discovery.item2Pricing.viewActiveOffer')}
                </a>
              </div>
            }
            {
              showMakeOffer({ listing, negotiations }) &&
              <CreateOfferButton
                listing={listing}
                disabled={isEndedListing(listing)}
                enableNudge={isLive(listing) && !showBuyItNowButton}
                onOfferSuccess={onOfferSuccess}
                userTotalOffersCount={userTotalOffersCount}
              />
            }
          </>
        }
        {
          !isEndedListingStatusIndicatorEnabled &&
          <div className="item2-pricing__watch">
            <WatchBadge
              listing={listing}
              displayStyle={DisplayStyle.ANIMATION_BUTTON}
              enableNudge={isLive(listing)}
              parentComponentName={WatchParentComponentNames.ItemPageActions}
              autoWatch={isLive(listing) && !!watchPrompt}
              useToast
            />
          </div>
        }

        <CompareOtherSellerLink listing={listing} />
      </div>
      {isEndedListing(listing) && !isEndedListingStatusIndicatorEnabled &&
        <div className="size-80 align-center">
          <p className="mb-0">
            {I18n.t('discovery.item2Pricing.watchThis')}
          </p>
        </div>
      }
      {!onFeaturedListing && !isEndedListingStatusIndicatorEnabled &&
        <Item2Stats
          listing={listing}
        />
      }
      {showOfferHistory({ listing, negotiations }) &&
        <div className="size-90 scaling-mt-2 align-center">
          <RCAlertBox type="plain">
            <SanitizedRender
              data-view-offer-history
              html={
                I18n.t('discovery.item2Pricing.viewOfferHistory', {
                  linkOpen: `<a href=${listingOffersPath(listing)}>`,
                  linkClose: '</a>',
                })
              }
            />
          </RCAlertBox>
        </div>
      }
      {/* Ended Listing Status Indicator Section */}
      {isEndedListingStatusIndicatorEnabled && !isLive(listing) &&
        <div className="item2-pricing__status-indicator-container">
          <div className="item2-sold-text">
            <p className="weight-bold align-center">
              {endedListingStatusIndicatorText(listing)}
            </p>
            <p className="align-center">
              {I18n.t('discovery.item2Pricing.statusIndicatorViewSimilar')}
            </p>
          </div>
          <Item2EndedListingSearchButton
            make={listing.make}
            model={listing.model}
            listingId={listing.id}
          />
          <div className="item2-pricing__watch">
            <WatchBadge
              listing={listing}
              displayStyle={DisplayStyle.ANIMATION_BUTTON}
              enableNudge={isLive(listing)}
              parentComponentName={WatchParentComponentNames.ItemPageActions}
              autoWatch={isLive(listing) && !!watchPrompt}
              useToast
            />
          </div>
          <div className="size-80 mobile-size-100">
            <p className="mb-0 mobile-align-left">
              {I18n.t('discovery.item2Pricing.watchThis')}
            </p>
          </div>
          <Item2Stats
            listing={listing}
            isEndedListingStatusIndicatorEnabled
          />
        </div>
      }
    </div>
  );
}

export default withUserContext(Item2Pricing);
